import { Directive, ElementRef, Renderer2, HostListener, Input } from '@angular/core';
import { FlexibleConnectedPositionStrategy, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { MatMenuTrigger } from '@angular/material/menu';
import { Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

// This is the original code link: https://stackblitz.com/edit/angular-jpgjdc-nqmyht?embed=1&file=app/center-matmenu.directive.ts
// StackOverflow link: https://stackoverflow.com/questions/54129512/center-mat-menu-center-matmenu-overlay-to-button

// Some parts of the original code is a little changed to be matched with our App and the performance we needed.
@Directive({
  selector: '[center-mat-menu]',
  standalone: false
})
export class CenterMatmenuDirective {
  overlayRef: OverlayRef;
  overlayConf: OverlayConfig;
  dropDown: HTMLElement;
  overlayPositionBox: HTMLElement;
  arrowDiv: HTMLDivElement;
  top: string;
  bottom: string;
  private _positionSub: Subscription;
  @Input('data-loading-subject') private dataLoadingSubject: Subject<any>;

  @Input('center-mat-menu') private menuTrigger: MatMenuTrigger;

  constructor(
    private _menuButton: ElementRef,
    private _renderer: Renderer2
  ) {}

  @HostListener('click', ['$event'])
  onclick(e) {
    if (this.dataLoadingSubject) {
      this.dataLoadingSubject.pipe(take(1)).subscribe(() => {
        this._init(e);
      });
    } else {
      this._init(e);
    }
  }
  private _init(e) {
    //menu not opened by keyboard down arrow, have to set this so MatMenuTrigger knows the menu was opened with a mouse click
    this.menuTrigger['_openedBy'] = e.button === 0 ? 'mouse' : undefined;

    this._overrideMatMenu();

    this.dropDown = this.overlayRef.overlayElement.children[0].children[0] as HTMLElement;
    this.overlayPositionBox = this.overlayRef.hostElement;

    const menuPanel = this.overlayRef.overlayElement.querySelector('.mat-mdc-menu-panel');
    if (menuPanel) {
      this._renderer.setStyle(menuPanel, 'max-width', '300px');
      this._renderer.setStyle(menuPanel, 'width', '300px');
      this._renderer.setStyle(menuPanel, 'overflow', 'visible');
      this._renderer.setStyle(menuPanel, 'position', 'unset');
    }
    setTimeout(() => {
      // Material picked above/below using the panel's original, height-constrained
      // size (before we removed its max-height/overflow above). Now that the panel
      // has grown to its real, unconstrained height, force the position strategy to
      // reconsider whether the preferred (e.g. "above") position still fits, instead
      // of just re-using whatever it picked on the first (possibly undersized) pass.
      const strat = this.overlayConf.positionStrategy as FlexibleConnectedPositionStrategy;
      strat.withLockedPosition(false);
      strat.apply();
      strat.withLockedPosition(true);

      // Read the (possibly just-flipped) top/bottom before drawing the arrow, so its
      // direction matches whichever side the menu actually ended up on.
      this._setOverlayPosition(this.dropDown, this.overlayPositionBox);
      if (menuPanel) {
        this._styleDropDown(this.dropDown, menuPanel);
      }
    }, 200);
  }

  private _getButtonVars = (): { buttonWidth: number; buttonLeft: number } => {
    const { width, left } = this._menuButton.nativeElement.getBoundingClientRect();
    return {
      buttonWidth: width,
      buttonLeft: left
    };
  };

  private _overrideMatMenu() {
    // Delegate the actual opening to the public API so we stay compatible with
    // Angular Material's internal implementation (which changes across versions),
    // then reach into the resulting overlay to apply our custom centering.
    this.menuTrigger.openMenu();

    this.overlayRef = this.menuTrigger['_overlayRef'];
    this.overlayConf = this.overlayRef.getConfig();

    let strat = this.overlayConf.positionStrategy as FlexibleConnectedPositionStrategy;
    this._positionSub = strat.positionChanges.subscribe(() => {
      this._setOverlayPosition(this.dropDown, this.overlayPositionBox);
    });

    this.menuTrigger.menuClosed.pipe(take(1)).subscribe(() => {
      this._positionSub?.unsubscribe();
      setTimeout(() => {
        const arrowParent = this.arrowDiv?.parentNode;
        if (arrowParent) {
          this._renderer.removeChild(arrowParent, this.arrowDiv);
        }
      }, 75);
    });
  }

  private _styleDropDown(dropDown: HTMLElement, menuPanel: Element) {
    let position = this.top ? 'up' : 'down';
    this.arrowDiv = this._renderer.createElement('div');
    this._renderer.addClass(this.arrowDiv, `dialog-arrow-${position}`);
    this._renderer.appendChild(menuPanel, this.arrowDiv);
    this._renderer.setStyle(this.arrowDiv, 'left', 140 + 'px');
    this._renderer.setStyle(
      this._renderer.parentNode(dropDown),
      'transform-origin',
      'center top 0px'
    );
  }

  private _setOverlayPosition(dropDown: HTMLElement, overlayPositionBox: HTMLElement) {
    this.top = this.overlayPositionBox.style.top;
    this.bottom = this.overlayPositionBox.style.bottom;
    const dropDownWidth = dropDown.offsetWidth;

    const { buttonLeft, buttonWidth } = this._getButtonVars();
    if (this.top) {
      this._renderer.setStyle(overlayPositionBox, 'top', parseFloat(this.top) + 8 + 'px');
    }
    if (this.bottom) {
      this._renderer.setStyle(overlayPositionBox, 'bottom', parseFloat(this.bottom) + 20 + 'px');
    }
    const overlayLeft = this.overlayPositionBox.style.left;
    const overlayRight = this.overlayPositionBox.style.right;

    const menuOffset = (dropDownWidth - buttonWidth) / 2; //how many pixels to left/right of button should the menu be
    this._renderer.setStyle(overlayPositionBox, 'height', '100%');
    if (overlayLeft && overlayLeft != '0px') {
      //material is trying to align this menu relative to the left side side.
      //need to tweak the 'left' style so that this menu is center aligned with the button that opened the menu
      const dropDownLeft = buttonLeft - menuOffset;
      this._renderer.setStyle(overlayPositionBox, 'left', dropDownLeft + 'px');
      return;
    }
    const right = overlayRight ? Number(overlayRight.replace(/px$/, '')) : 0;
    if (!right) {
      //left and right aren't set or left is zero
      //happens at very small screen sizes
      return;
    }

    //material is trying to align this menu relative to the right side.
    //need to tweak the 'right' style so that this menu is center aligned with the button that opened the menu
    //previously these was a bug where we were always setting the 'left' style, but that didn't work well with how material does positioning

    //without this component regular material aligns the left sides of the button and menu.
    //so at this point ('with regular material'), the right side of menu would start getting covered up by right side of window,
    //when that happens, material switches and aligns the right side of the menu with the right side of the button.
    //knowing this we can tell by the original 'right' style how many pixels to set the menu to center align it
    const menuToRightOfWindowBy = right - menuOffset;
    const rightStyle = menuToRightOfWindowBy > 25 ? `${menuToRightOfWindowBy}px` : '25px';

    this._renderer.setStyle(overlayPositionBox, 'right', rightStyle);
  }
}
